// src/plugins/mdi.js
// Migration : vue-material-design-icons → @iconify/vue
// Les icônes sont chargées globalement via src/icons/mdi.js (addCollection)

import { h } from 'vue'
import { Icon } from '@iconify/vue'

// Mapping pour conversion el-icon-* vers MDI
export const iconMapping = {
    "el-icon-search": "Magnify",
    "el-icon-plus": "Plus",
    "el-icon-delete": "Delete",
    "el-icon-check": "Check",
    "el-icon-close": "Close",
    "el-icon-user": "Account",
    "el-icon-back": "ArrowLeft",
    "el-icon-right": "ArrowRight",
    "el-icon-edit": "PencilOutline",
    "el-icon-refresh": "Refresh",
    "el-icon-upload": "Upload",
    "el-icon-download": "Download",
    "el-icon-info": "Information",
    "el-icon-warning-outline": "AlertOutline",
    "el-icon-question": "HelpCircle",
    "el-icon-loading": "Loading",
    "el-icon-circle-check": "CheckCircle",
    "el-icon-view": "Eye",
    "el-icon-setting": "Cog",
    "el-icon-phone-outline": "Phone",
    "el-icon-connection": "Link",
    "el-icon-circle-plus-outline": "PlusCircleOutline",
    "el-icon-s-data": "ChartBar",
    "el-icon-s-opportunity": "Lightbulb",
    "el-icon-mouse": "Mouse",
    "el-icon-magic-stick": "AutoFix",
    "el-icon-copy-document": "ContentCopy",
    "el-icon-turn-off": "Power",
    "el-icon-s-order": "FileDocument",
    "el-icon-set-up": "Cog",
    "el-icon-open": "OpenInNew",
    "el-icon-document": "FileDocument",
    "el-icon-folder": "Folder",
    "el-icon-arrow-down": "ArrowDown",
    "el-icon-arrow-up": "ArrowUp",
    "el-icon-warning": "Alert",
    "el-icon-error": "CloseCircle",
    "el-icon-success": "CheckCircle",
    "el-icon-remove": "Minus",
    "el-icon-circle-close": "CloseCircle",
    "el-icon-minus": "Minus",
    "el-icon-zoom-in": "MagnifyPlus",
    "el-icon-zoom-out": "MagnifyMinus",
    "el-icon-lock": "Lock",
    "el-icon-unlock": "LockOpen",
    "el-icon-key": "Key",
    "el-icon-message": "Message",
    "el-icon-chat": "Chat",
    "el-icon-picture": "Image",
    "el-icon-camera": "Camera",
    "el-icon-video-camera": "Video",
    "el-icon-headset": "Headset",
    "el-icon-location": "MapMarker",
    "el-icon-bell": "Bell",
    "el-icon-more": "DotsHorizontal",
    "el-icon-more-outline": "DotsHorizontal",
    "el-icon-star-on": "Star",
    "el-icon-star-off": "StarOutline",
    "el-icon-goods": "Package",
    "el-icon-share": "Share",
    "el-icon-printer": "Printer",
    "el-icon-tickets": "Ticket",
    "el-icon-link": "Link",
    "el-icon-sort": "Sort",
    "el-icon-rank": "ChartLine",
    "el-icon-bottom": "ChevronDown",
    "el-icon-top": "ChevronUp",
    "el-icon-finished": "CheckCircle",
    "el-icon-remove-outline": "MinusCircleOutline",
    "el-icon-circle-plus": "PlusCircle",
    "el-icon-platform-eleme": "Silverware",
    "el-icon-eleme": "Silverware",
    "el-icon-collection": "FolderMultiple",
    "el-icon-phone": "Phone",
    "el-icon-upload2": "Upload",
    "el-icon-scissors": "ContentCut",
    "el-icon-date": "Calendar",
    "el-icon-time": "Clock",
    "el-icon-files": "FileMultiple",
    "el-icon-notebook": "NotebookOutline",
    "el-icon-expand": "ArrowExpandAll",
    "el-icon-collapse": "ArrowCollapseAll",
    "el-icon-place": "MapMarker",
    "el-icon-mobile": "Cellphone",
    "el-icon-discount": "SaleOutline",
    "el-icon-location-information": "MapMarkerRadius",
    "el-icon-position": "Crosshairs",
    // Icônes supplémentaires pour DashRenouvelement
    "el-icon-document-checked": "FileDocumentCheck",
    "el-icon-coin": "Cash",
    "el-icon-star": "Star",
    email: "Email",
    "file-document-multiple": "FileDocumentMultiple"
};

// Helper : normalise un nom d'icône en format mdi:
function normalizeIconName(name) {
    if (typeof name !== 'string' || name.length === 0) return null
    // Tout nom déjà préfixé passe tel quel : `mdi:`, mais aussi les collections
    // maison enregistrées via addIcon (`prosoluce:luce`). Sans ce test, elles
    // devenaient `mdi:prosoluce:luce` et ne s'affichaient nulle part.
    if (name.includes(':')) return name
    // PascalCase → kebab : PackagePlus → package-plus
    const kebab = name.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase()
    return `mdi:${kebab}`
}

// Helper : crée un composant icône global avec @iconify/vue
function createGlobalIcon(mdiName, size = 24) {
    return {
        name: mdiName.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase() + '-icon',
        props: { size: { type: [Number, String], default: size } },
        setup(props) {
            return () => h(Icon, {
                icon: normalizeIconName(mdiName),
                width: Number(props.size),
                height: Number(props.size),
            })
        },
    }
}

// Composant universel MdiIcon
const MdiIcon = {
    name: 'MdiIcon',
    props: {
        name: { type: String, default: null },
        size: { type: [Number, String], default: 24 },
    },
    setup(props) {
        return () => {
            const icon = normalizeIconName(props.name)
            if (!icon) return null
            return h(Icon, {
                icon,
                width: Number(props.size),
                height: Number(props.size),
            })
        }
    },
}

export default {
    install(app) {
        // Enregistrer le composant universel
        app.component('MdiIcon', MdiIcon)

        // Enregistrer les icônes pré-chargées (compatibilité backward)
        app.component('MenuIcon', createGlobalIcon('Menu'))
        app.component('HistoryIcon', createGlobalIcon('History'))
        app.component('AccountIcon', createGlobalIcon('Account'))
        app.component('MagnifyIcon', createGlobalIcon('Magnify'))
        app.component('ArrowLeftIcon', createGlobalIcon('ArrowLeft'))
        app.component('ArrowRightIcon', createGlobalIcon('ArrowRight'))
        app.component('PlusIcon', createGlobalIcon('Plus'))
        app.component('DeleteIcon', createGlobalIcon('Delete'))
        app.component('CheckIcon', createGlobalIcon('Check'))
        app.component('CloseIcon', createGlobalIcon('Close'))
        app.component('FullscreenIcon', createGlobalIcon('Fullscreen'))
        app.component('CogIcon', createGlobalIcon('Cog'))
        app.component('InformationIcon', createGlobalIcon('Information'))
        app.component('MessageIcon', createGlobalIcon('Message'))
        app.component('PrinterIcon', createGlobalIcon('Printer'))
        app.component('LinkIcon', createGlobalIcon('Link'))
        app.component('EyeIcon', createGlobalIcon('Eye'))
        app.component('EyeOffIcon', createGlobalIcon('EyeOff'))
        app.component('DownloadIcon', createGlobalIcon('Download'))
        app.component('RefreshIcon', createGlobalIcon('Refresh'))
        app.component('MouseIcon', createGlobalIcon('Mouse'))
        app.component('LightbulbIcon', createGlobalIcon('Lightbulb'))
        app.component('PowerIcon', createGlobalIcon('Power'))
        app.component('ContentCopyIcon', createGlobalIcon('ContentCopy'))
        app.component('PlusCircleOutlineIcon', createGlobalIcon('PlusCircleOutline'))
        app.component('PhoneIcon', createGlobalIcon('Phone'))
        app.component('ReceiptIcon', createGlobalIcon('Receipt'))
        app.component('CashIcon', createGlobalIcon('Cash'))
        app.component('BookIcon', createGlobalIcon('Book'))
        app.component('PhoneForwardIcon', createGlobalIcon('PhoneForward'))
        app.component('ChartBarIcon', createGlobalIcon('ChartBar'))
        app.component('ChartPieIcon', createGlobalIcon('ChartPie'))
        app.component('HeadsetIcon', createGlobalIcon('Headset'))
        app.component('SendIcon', createGlobalIcon('Send'))
        app.component('PackageVariantIcon', createGlobalIcon('PackageVariant'))
        app.component('AccountGroupIcon', createGlobalIcon('AccountGroup'))
        app.component('ClockAlertOutlineIcon', createGlobalIcon('ClockAlertOutline'))
        app.component('FileDocumentOutlineIcon', createGlobalIcon('FileDocumentOutline'))
        app.component('CheckDecagramIcon', createGlobalIcon('CheckDecagram'))
        app.component('CashMultipleIcon', createGlobalIcon('CashMultiple'))
        app.component('CartOutlineIcon', createGlobalIcon('CartOutline'))
        app.component('CurrencyEurIcon', createGlobalIcon('CurrencyEur'))
        app.component('TrendingUpIcon', createGlobalIcon('TrendingUp'))
        app.component('TimerSandIcon', createGlobalIcon('TimerSand'))
        app.component('CloseCircleOutlineIcon', createGlobalIcon('CloseCircleOutline'))
        app.component('ViewDashboardIcon', createGlobalIcon('ViewDashboard'))
        app.component('NoteMultipleIcon', createGlobalIcon('NoteMultiple'))
        app.component('PackageIcon', createGlobalIcon('Package'))
        app.component('CreditCardIcon', createGlobalIcon('CreditCard'))
        app.component('DevicesIcon', createGlobalIcon('Devices'))
        app.component('BookAccountOutlineIcon', createGlobalIcon('BookAccountOutline'))
        app.component('SitemapIcon', createGlobalIcon('Sitemap'))
        app.component('AccountDetailsIcon', createGlobalIcon('AccountDetails'))
        app.component('AlertCircleOutlineIcon', createGlobalIcon('AlertCircleOutline'))
        app.component('PhoneClassicIcon', createGlobalIcon('PhoneClassic'))
        app.component('CalendarIcon', createGlobalIcon('Calendar'))
        app.component('CalendarClockIcon', createGlobalIcon('CalendarClock'))
        app.component('DotsVerticalIcon', createGlobalIcon('DotsVertical'))
        app.component('ViewGridIcon', createGlobalIcon('ViewGrid'))
        app.component('FormatListBulletedIcon', createGlobalIcon('FormatListBulleted'))
        app.component('UploadIcon', createGlobalIcon('Upload'))

        // Alias PascalCase pour icônes utilisées comme tag <XxxIcon /> dans les templates
        app.component('DomainIcon', createGlobalIcon('Domain'))
        app.component('HandshakeIcon', createGlobalIcon('Handshake'))
        app.component('FolderIcon', createGlobalIcon('Folder'))
        app.component('MonitorIcon', createGlobalIcon('Monitor'))
        app.component('EditIcon', createGlobalIcon('Pencil'))
        app.component('NetworkIcon', createGlobalIcon('LanConnect'))
        app.component('UserFilledIcon', createGlobalIcon('Account'))
        app.component('ServiceIcon', createGlobalIcon('RoomService'))
        app.component('FilterIcon', createGlobalIcon('Filter'))
        app.component('MapIcon', createGlobalIcon('Map'))
        app.component('FactoryIcon', createGlobalIcon('Factory'))
        app.component('LockIcon', createGlobalIcon('Lock'))
        app.component('SignatureIcon', createGlobalIcon('DrawPen'))
        app.component('CopyDocumentIcon', createGlobalIcon('ContentCopy'))
        app.component('DocumentIcon', createGlobalIcon('FileDocument'))
        app.component('PlayIcon', createGlobalIcon('Play'))
        app.component('PauseIcon', createGlobalIcon('Pause'))
        app.component('CircleIcon', createGlobalIcon('Circle'))
        app.component('SearchIcon', createGlobalIcon('Magnify'))

        // Auto-register: créer des composants pour toutes les icônes du mapping
        Object.entries(iconMapping).forEach(([key, mdiName]) => {
            const kebabName = key
                .replace(/el-icon-/, '')
                .replace(/([a-z])([A-Z])/g, '$1-$2')
                .toLowerCase() + '-icon'

            if (!app.component(kebabName)) {
                app.component(kebabName, createGlobalIcon(mdiName))
            }
        })

        // Icônes additionnelles (non couvertes par le mapping)
        const additionalIcons = [
            'FileDocument', 'KeyChain', 'Alert', 'PencilOutline',
            'FileDocumentCheck', 'Calendar', 'Clock', 'Star', 'Email',
            'FileDocumentMultiple', 'ChevronUp', 'ChevronDown',
            'MinusCircleOutline', 'AlertOutline', 'HelpCircle', 'Loading',
            'SubdirectoryArrowRight', 'ChevronRight', 'ChevronLeft',
            'FormatListNumbered', 'CodeJson', 'CalendarMultiselectOutline',
            'TrashCanOutline', 'PartyPopper', 'Wrench', 'MapMarkerOff',
            'Upload', 'BookOpenPageVariant', 'ShareVariant', 'AccountMultiple',
            'Pencil', 'DeleteSweep', 'Cellphone', 'Home', 'DotsHorizontal',
            'SwapHorizontal', 'PhonePlus', 'CheckCircle', 'CloseCircle',
            'VectorPolylineEdit', 'AccountArrowLeftOutline', 'MapPlus',
            'MapSearch', 'PencilPlusOutline', 'ArrowUp', 'AccountPlus',
            'LinkOff', 'OpenInNew', 'Link', 'PlusCircle', 'ContentSave',
            'CodeBraces', 'AutoFix', 'AlertCircle', 'CodeJson', 'Earth',
            'Web', 'Filter', 'Flag', 'MapMarker', 'InformationOutline',
            'CurrencyEur', 'ClipboardCheck', 'PhoneSettings', 'ClockOutline',
            'LinkVariantPlus', 'CheckAll', 'FileDelimited', 'ArrowDown',
            'FileMultiple', 'VolumeHigh', 'ClipboardText', 'FileSign',
            'FilePdfBox', 'FileImage', 'FileMusic', 'FileDocumentOutline',
            'FileImport', 'Download', 'Eye', 'FolderOpen', 'Play', 'Pause',
            'Handshake', 'NoteText', 'Store', 'AccountTie',
            'AccountArrowRight', 'ViewAgenda', 'ViewList', 'Tag', 'Bug',
        ]
        additionalIcons.forEach(iconName => {
            const kebabName = iconName
                .replace(/([a-z])([A-Z])/g, '$1-$2')
                .toLowerCase() + '-icon'
            if (!app.component(kebabName)) {
                app.component(kebabName, createGlobalIcon(iconName))
            }
        })
    },
}
